import React, { forwardRef } from 'react';

import MuiButton from '@material-ui/core/Button';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import { alpha, darken } from '@material-ui/core/styles/colorManipulator';
import capitalize from '@material-ui/core/utils/capitalize';
import PropTypes from 'prop-types';

const SEMANTIC_COLORS = ['info', 'warning', 'success', 'error'];
const CONTEXT_COLORS = ['institutions', 'sellers'];
const CUSTOM_COLORS = [...SEMANTIC_COLORS, ...CONTEXT_COLORS];
const MUI_COLORS = ['default', 'inherit', 'primary', 'secondary'];
const VARIANTS = ['contained', 'outlined', 'text'];

function getPalette(theme, color) {
  return CONTEXT_COLORS.includes(color) ? theme.palette.context?.[color] : theme.palette[color];
}

const useStyles = makeStyles(theme =>
  CUSTOM_COLORS.reduce((styles, color) => {
    const palette = getPalette(theme, color);
    if (!palette?.main) return styles;

    const { main, contrastText } = palette;
    const suffix = capitalize(color);

    return {
      ...styles,
      [`contained${suffix}`]: {
        color: contrastText || theme.palette.getContrastText(main),
        backgroundColor: main,
        '&:hover': {
          backgroundColor: darken(main, 0.1),
        },
      },
      [`outlined${suffix}`]: {
        color: main,
        borderColor: main,
        '&:hover': {
          borderColor: main,
          backgroundColor: alpha(main, 0.08),
        },
      },
      [`text${suffix}`]: {
        color: main,
        '&:hover': {
          backgroundColor: alpha(main, 0.08),
        },
      },
    };
  }, {}),
);

const Button = forwardRef(({ loading, onClick, startIcon, endIcon, ...muiProps }, ref) => {
  const classes = useStyles();

  const variant = muiProps.variant || 'contained';
  const isCustomColor = CUSTOM_COLORS.includes(muiProps.color);

  let resolvedMuiProps = muiProps;

  if (isCustomColor) {
    resolvedMuiProps = {
      ...muiProps,
      color: 'primary',
      className: [muiProps.className, classes[`${variant}${capitalize(muiProps.color)}`]]
        .filter(Boolean)
        .join(' '),
    };
  }

  const defaultProps = {
    variant: 'contained',
    color: 'primary',
    disableElevation: true,
  };

  return loading ? (
    <MuiButton ref={ref} {...defaultProps} {...resolvedMuiProps}>
      <MuiCircularProgress color="inherit" size={22} />
    </MuiButton>
  ) : (
    <MuiButton
      ref={ref}
      {...defaultProps}
      endIcon={endIcon}
      startIcon={startIcon}
      onClick={onClick}
      {...resolvedMuiProps}
    />
  );
});

Button.propTypes = {
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  startIcon: PropTypes.any,
  endIcon: PropTypes.any,
  color: PropTypes.oneOf([...MUI_COLORS, ...CUSTOM_COLORS]),
  variant: PropTypes.oneOf(VARIANTS),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};

export default Button;
